import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import Button from './Button';
import Icon from './Icon';
import SearchableSelect, { SearchableMultiSelect } from './SearchableSelect';
import {brandAPI, productAPI, mediatorAPI, sheetAPI} from '../../services/api';
import toast from 'react-hot-toast';
import { todayIsoDate, toIsoRangeStart, toIsoRangeEnd } from '../../utils/dateTime';

const ExportModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [mediators, setMediators] = useState([]);
  const [orderStatuses, setOrderStatuses] = useState([]);
  const [selectedOrderStatuses, setSelectedOrderStatuses] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedMediators, setSelectedMediators] = useState([]);
  const [orderIdFilter, setOrderIdFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [exportType, setExportType] = useState('SELF'); // SELF, MEDIATOR, SELLER
  const [selectedMediator, setSelectedMediator] = useState('');
  const [fileName, setFileName] = useState('');
  const brandOptions = brands.map((brand) => ({ value: brand.id, label: brand.brandName }));
  const productOptions = products.map((product) => ({ value: product.id, label: `${product.productCode} - ${product.productName}` }));
  const orderStatusOptions = orderStatuses.map((status) => ({ value: status.code, label: status.label }));
  const mediatorOptions = mediators.map((mediator) => ({
    value: mediator.id,
    label: mediator.mediatorName || mediator.fullName || mediator.username || `Mediator ${mediator.id}`
  }));

  useEffect(() => {
    const statuses = [
      { code: 'ORDER_FORM_FILLED', label: 'Order Form Filled' },
      { code: 'REFUND_FORM_FILLED', label: 'Refund Form Filled' },
      { code: 'APPROVED', label: 'Approved' },
      { code: 'REJECTED', label: 'Rejected' },
      { code: 'ORDER_FORM_REFILL_REQUEST', label: 'Order Form Refill Request' },
      { code: 'REJECT_AND_REFILL', label: 'Reject and Refill' },
      { code: 'REFUND_FORM_REFILL_REQUEST', label: 'Refund Form Refill Request' },
      { code: 'PAID', label: 'Paid' },
      { code: 'PAID_TO_MEDIATOR', label: 'Order Paid To Mediator' },
      { code: 'PAID_TO_BUYER', label: 'Order Paid To Buyer' }
    ];

    setOrderStatuses(statuses);
  }, []);
  useEffect(() => {
    if (isOpen) {
      fetchBrands();
      fetchMediators();
      resetForm();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedBrands.length > 0) {
      fetchProductsByBrands(selectedBrands);
    } else {
      setProducts([]);
      setSelectedProducts([]);
    }
  }, [selectedBrands]);

  const resetForm = () => {
    setStep(1);
    setSelectedBrands([]);
    setSelectedProducts([]);
    setSelectedOrderStatuses([]);
    setSelectedMediators([]);
    setOrderIdFilter('');
    setStartDate('');
    setEndDate('');
    setExportType('SELF');
    setSelectedMediator('');
    setFileName(`Orders_Export_${todayIsoDate()}`);
  };

  const fetchBrands = async () => {
    try {
      const response = await brandAPI.getActiveBrands();
      setBrands(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch brands');
    }
  };

  const fetchMediators = async () => {
    try {
      const response = await mediatorAPI.getAllMediators();
      setMediators(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch mediators');
    }
  };

  const fetchProductsByBrands = async (brandIds) => {
    try {
      const response = await productAPI.getProductsByBrands(brandIds);
      setProducts(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch products');
    }
  };

  const handleNext = () => {
    if (selectedBrands.length === 0) {
      toast.error('Please select at least one brand');
      return;
    }
    // Removed product selection requirement as requested
    setStep(2);
  };

  const handleExport = async () => {
    if (!fileName) {
      toast.error('File name is required');
      return;
    }
    if (exportType === 'MEDIATOR' && !selectedMediator) {
      toast.error('Please select a mediator');
      return;
    }

    setLoading(true);
      console.log(selectedBrands, "selectedBrands");

    try {
      console.log(selectedBrands, "selectedBrands");
      const filterDto = {
        productId: selectedProducts.length > 0 ? selectedProducts : null,
        brandId: selectedBrands,
        orderStatus: selectedOrderStatuses.length > 0 ? selectedOrderStatuses : null,
        fileName: fileName,
        mediatorId: exportType === 'MEDIATOR' ? selectedMediator : (selectedMediators.length === 1 ? selectedMediators[0] : null),
        sheetFor: exportType,
        orderId: orderIdFilter || null,
        startDate: toIsoRangeStart(startDate),
        endDate: toIsoRangeEnd(endDate)
      };

      const response = await sheetAPI.generateSheet(filterDto);
      console.log(response, "response");
      if (response.data.success) {
        toast.success(response.data.message || 'Sheet generated successfully');
        if (response.data.data?.sheetUrl || response.data.data?.fileUrl) {
          window.open(response.data.data.sheetUrl || response.data.data.fileUrl, '_blank');
        }
        onClose();
      } else {
        toast.error(response.data.message || 'Failed to generate sheet');
      }
    } catch (error) {
      const timedOut = error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '');
      toast.error(
        timedOut
          ? 'Sheet generation is taking longer than expected. Check the Sheets list — it may still complete.'
          : (error.response?.data?.message || 'Error generating sheet')
      );
    } finally {
      setLoading(false);
    }
  };



  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Export Orders Sheet" width={520}>
      <div style={{ padding: '20px' }}>
        {step === 1 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: 'var(--text)' }}>Select Brands</label>
              <SearchableMultiSelect
                options={brandOptions}
                selectedValues={selectedBrands}
                onChange={setSelectedBrands}
                placeholder="Search and select brands..."
                showSelectAll
              />
              <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '6px' }}>Select one or more brands to filter products</p>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: 'var(--text)' }}>Select Products</label>
              <SearchableMultiSelect
                options={productOptions}
                selectedValues={selectedProducts}
                onChange={setSelectedProducts}
                placeholder={selectedBrands.length === 0 ? "Select a brand first" : "Search and select products..."}
                disabled={selectedBrands.length === 0}
                disabledText="Select a brand first"
                showSelectAll
              />
              <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '6px' }}>
                {selectedBrands.length === 0 ? 'Choose a brand to see available products' : 'Optional: Leave empty to export all products for selected brands'}
              </p>
            </div><div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: 'var(--text)' }}>
              Select Order Status
            </label>

            <SearchableMultiSelect
              options={orderStatusOptions}
              selectedValues={selectedOrderStatuses}
              onChange={setSelectedOrderStatuses}
              placeholder="Select order status..."
            />

            <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '6px' }}>
              Optional: Filter orders by status
            </p>
          </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: 'var(--text)' }}>Order ID</label>
              <input
                type="text"
                value={orderIdFilter}
                onChange={(e) => setOrderIdFilter(e.target.value)}
                placeholder="Filter by specific order ID..."
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '14px' }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: 'var(--text)' }}>Creation Date From</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '14px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: 'var(--text)' }}>Creation Date To</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '14px' }}
                />
              </div>
            </div>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '-12px' }}>
              Optional: filter exported orders by when they were created in the system
            </p>

            {exportType !== 'MEDIATOR' && (
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: 'var(--text)' }}>Filter by Mediator (Optional)</label>
                <SearchableSelect
                  options={mediatorOptions}
                  value={selectedMediators[0] || ''}
                  onChange={(value) => setSelectedMediators(value ? [value] : [])}
                  placeholder="All mediators"
                  searchPlaceholder="Type mediator name..."
                  noOptionsText="No mediators found"
                />
              </div>
            )}


            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
              <Button onClick={handleNext} disabled={selectedBrands.length === 0} style={{ padding: '10px 24px' }}>
                Next <Icon name="arrow-right" size={16} style={{ marginLeft: '8px' }} />
              </Button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: 'var(--text)' }}>File Name</label>
              <div style={{ position: 'relative' }}>
                <input 
                  type="text" 
                  value={fileName} 
                  onChange={(e) => setFileName(e.target.value)}
                  placeholder="Enter file name..."
                  style={{ 
                    width: '100%', 
                    padding: '10px 12px', 
                    borderRadius: '8px', 
                    border: '1px solid var(--border)',
                    background: 'var(--bg)',
                    color: 'var(--text)',
                    fontSize: '14px'
                  }}
                />
                <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: '13px' }}>.xlsx</div>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600', fontSize: '14px', color: 'var(--text)' }}>Generate Sheet For</label>
              <div style={{ display: 'flex', gap: '16px', background: 'var(--bg-muted)', padding: '4px', borderRadius: '10px' }}>
                {['MEDIATOR', 'SELF', 'SELLER'].map((type) => (
                  <button
                    key={type}
                    onClick={() => setExportType(type)}
                    style={{
                      flex: 1,
                      padding: '8px',
                      borderRadius: '8px',
                      border: 'none',
                      background: exportType === type ? 'var(--bg)' : 'transparent',
                      color: exportType === type ? 'var(--primary)' : 'var(--text-muted)',
                      fontSize: '13px',
                      fontWeight: exportType === type ? '600' : '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: exportType === type ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                    }}
                  >
                    {type.charAt(0) + type.slice(1).toLowerCase()}
                  </button>
                ))}
              </div>
            </div>

            {exportType === 'MEDIATOR' && (
              <div style={{ animation: 'slideDown 0.3s ease-out' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: 'var(--text)' }}>Select Mediator</label>
                <SearchableSelect
                  options={mediatorOptions}
                  value={selectedMediator}
                  onChange={setSelectedMediator}
                  placeholder="Choose a mediator..."
                  searchPlaceholder="Type mediator name..."
                  noOptionsText="No mediators found"
                />
              </div>
            )}

            {/* Summary Section */}
            <div style={{ 
              background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)', 
              border: '1px solid #e2e8f0', 
              borderRadius: '12px', 
              padding: '16px',
              marginTop: '8px'
            }}>
              <h4 style={{ 
                margin: '0 0 12px 0', 
                fontSize: '14px', 
                fontWeight: '600', 
                color: '#374151',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Icon name="file-text" size={16} style={{ color: '#6b7280' }} />
                Export Summary
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6b7280' }}>File Name:</span>
                  <span style={{ fontWeight: '600', color: '#1f2937' }}>{fileName}.xlsx</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6b7280' }}>Sheet For:</span>
                  <span style={{ fontWeight: '600', color: '#1f2937' }}>{exportType.charAt(0) + exportType.slice(1).toLowerCase()}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6b7280' }}>Brands:</span>
                  <span style={{ fontWeight: '600', color: '#1f2937' }}>
                    {selectedBrands.length === 1 
                      ? brands.find(b => b.id === selectedBrands[0])?.brandName || 'Unknown'
                      : `${selectedBrands.length} brands selected`
                    }
                  </span>
                </div>
                {selectedProducts.length > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280' }}>Products:</span>
                    <span style={{ fontWeight: '600', color: '#1f2937' }}>
                      {selectedProducts.length === 1 
                        ? products.find(p => p.id === selectedProducts[0])?.productName || 'Unknown'
                        : `${selectedProducts.length} products selected`
                      }
                    </span>
                  </div>
                )}
                {exportType === 'MEDIATOR' && selectedMediator && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280' }}>Mediator:</span>
                    <span style={{ fontWeight: '600', color: '#1f2937' }}>
                      {mediators.find(m => m.id === selectedMediator)?.mediatorName || 'Unknown'}
                    </span>
                  </div>
                )}
              </div>
              <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e2e8f0', fontSize: '12px', color: '#64748b', lineHeight: '1.5' }}>
                Exported sheets use <strong>Payout Type</strong> and <strong>Payout Value</strong> labels. Payout value may represent either less amount or commission depending on the payout type.
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
              <Button variant="secondary" onClick={() => setStep(1)} style={{ padding: '10px 20px' }}>
                <Icon name="arrow-left" size={16} style={{ marginRight: '8px' }} /> Back
              </Button>
              <Button onClick={handleExport} loading={loading} style={{ padding: '10px 24px' }}>
                Export Sheet <Icon name="download" size={16} style={{ marginLeft: '8px' }} />
              </Button>
            </div>
          </div>
        )}
      </div>
      <style jsx>{`
        .hover-bg:hover {
          background-color: var(--bg-muted) !important;
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </Modal>
  );
};

export default ExportModal;
